import { useContext, useMemo } from "react";

import { getExtensionValue } from "@medplum/core";
import { Questionnaire } from "@medplum/fhirtypes";

import { Box, Stack, Textarea } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { PhysicalExamErrorsContext } from "@/contexts/physicalExamsError";
import { Extensions } from "@/fhir/enums/extensions";

import PhysicalExamItem from "./PhysicalExamItem";
import PhysicalExamNotesViewer from "./PhysicalExamNotesViewer";
import PhysicalExamRadioItem from "./PhysicalExamRadioItem";
import PhysicalExamSection from "./PhysicalExamSection";
import PhysicalExamSectionViewer from "./PhysicalExamSectionViewer";
import { PhysicalExamQuestionTypeRecord, PhysicalExamSectionRecord } from "./types/physical-exams";
import { NewbornPostpartumPhysicalExam } from "./zod-schemas/newborn-postpartum-physical-exam";
import {
  PhysicalExamForm,
  PhysicalExamSectionField,
  SingleChoiceField,
} from "./zod-schemas/physical-exams";

// Type for section values that can be extracted from initialValues
type SectionValue =
  | PhysicalExamSectionField
  | SingleChoiceField
  | NewbornPostpartumPhysicalExam["sections"][string];

function hasSingleChoice(section: SectionValue): section is SingleChoiceField {
  return "singleChoice" in section;
}

function hasNormalFields(
  section: SectionValue,
): section is PhysicalExamSectionField | NewbornPostpartumPhysicalExam["sections"][string] {
  return "normal" in section || "notNormal" in section;
}

interface PhysicalExamListProps {
  sections: Record<string, PhysicalExamSectionRecord | PhysicalExamQuestionTypeRecord>;
  form: UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>;
  initialValues?: PhysicalExamForm | NewbornPostpartumPhysicalExam;
  editable?: boolean;
  questionnaire?: Questionnaire;
  hideDetails?: boolean; // Add prop to hide "Provide more detail" functionality
  hideGeneralNotes?: boolean; // Add prop to hide the final bottom notes section
}

export default function PhysicalExamList({
  sections,
  form,
  initialValues,
  editable,
  questionnaire,
  hideDetails = false,
  hideGeneralNotes = false,
}: PhysicalExamListProps) {
  const { removePhysicalExamError } = useContext(PhysicalExamErrorsContext);
  const toggleDecline = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string,
    targetName: string,
  ) => {
    form.setFieldValue(`sections.${name}.declined`, event.target.checked);
    removePhysicalExamError(targetName);
  };
  const notes = (form.values as PhysicalExamForm).notes.trim();
  const showNotesViewer = !editable && !!notes && !hideGeneralNotes;

  // Check if questionnaire has notes fields within sections
  const hasQuestionnaireNotes = useMemo(
    () =>
      questionnaire?.item?.some((section) =>
        section.item?.some((item) => item.linkId?.includes("Notes")),
      ),
    [questionnaire],
  );

  const singleChoiceFields = useMemo(() => {
    return Object.keys(sections).map((field) => {
      const section = sections[field] as PhysicalExamSectionRecord | PhysicalExamQuestionTypeRecord;

      const questionnaireItem = questionnaire?.item?.find((item) => item.text === section.label);
      const type = getExtensionValue(questionnaireItem, Extensions.PhysicalExamQuestionType);
      return { section, type };
    });
  }, [sections, questionnaire]);

  if (!editable) {
    return (
      <>
        <Stack gap="md">
          {Object.keys(sections).map((field) => {
            const section = sections[field] as
              | PhysicalExamSectionRecord
              | PhysicalExamQuestionTypeRecord;
            const values = form.values.sections[field] as PhysicalExamForm["sections"][string];

            return (
              <PhysicalExamSectionViewer
                key={field}
                form={form}
                field={field}
                title={section.label}
                isDeclined={values?.declined}
              />
            );
          })}
        </Stack>
        {showNotesViewer && <PhysicalExamNotesViewer notes={notes} />}
      </>
    );
  }

  return (
    <>
      {Object.keys(sections).map((field) => {
        const section = sections[field] as
          | PhysicalExamSectionRecord
          | PhysicalExamQuestionTypeRecord;
        const values = form.values.sections[field] as PhysicalExamForm["sections"][string];

        const normalName = `sections.${field}.normal`;
        const notNormalName = `sections.${field}.notNormal`;
        const singleChoiceFieldName = `sections.${field}.singleChoice`;
        const isSingleChoice =
          singleChoiceFields.find((field) => field.section.label === section.label)?.type ===
          "single";

        return (
          <>
            {isSingleChoice ? (
              <PhysicalExamSection
                key={field}
                title={section.label}
                isDeclined={values.declined}
                onDecline={(e) => toggleDecline(e, field, section.label)}
              >
                <PhysicalExamRadioItem
                  form={form}
                  hideDetails={hideDetails}
                  disabled={values.declined}
                  name={singleChoiceFieldName}
                  sectionLabel={section.label}
                  url={(section as PhysicalExamQuestionTypeRecord).singleValueSet}
                  initial={
                    "sections" in (initialValues || {})
                      ? (() => {
                          const section = initialValues?.sections[field] as SectionValue;
                          return hasSingleChoice(section) ? section.singleChoice : undefined;
                        })()
                      : undefined
                  }
                />
              </PhysicalExamSection>
            ) : (
              <PhysicalExamSection
                key={field}
                title={section.label}
                isDeclined={values.declined}
                onDecline={(e) => toggleDecline(e, field, section.label)}
              >
                <PhysicalExamItem
                  form={form}
                  name={normalName}
                  label="Normal findings"
                  hideDetails={hideDetails}
                  disabled={values.declined}
                  sectionLabel={section.label}
                  url={(section as PhysicalExamSectionRecord).normalValueSet}
                  initial={
                    "sections" in (initialValues || {})
                      ? (() => {
                          const section = initialValues?.sections[field] as SectionValue;
                          return hasNormalFields(section) ? section.normal : undefined;
                        })()
                      : undefined
                  }
                />
                <PhysicalExamItem
                  form={form}
                  name={notNormalName}
                  hideDetails={hideDetails}
                  disabled={values.declined}
                  label="Not normal findings"
                  sectionLabel={section.label}
                  url={(section as PhysicalExamSectionRecord).notNormalValueSet}
                  initial={
                    "sections" in (initialValues || {})
                      ? (() => {
                          const section = initialValues?.sections[field] as SectionValue;
                          return hasNormalFields(section) ? section.notNormal : undefined;
                        })()
                      : undefined
                  }
                />
                {/* Conditional notes field - appears after the last PhysicalExamItem */}
                {hasQuestionnaireNotes && !values.declined && (
                  <Box mt="lg">
                    <Textarea
                      label="Notes"
                      placeholder="Additional notes for this section..."
                      {...form.getInputProps(`sections.${field}.notes`)}
                    />
                  </Box>
                )}
              </PhysicalExamSection>
            )}
          </>
        );
      })}
      {!hideGeneralNotes && <Textarea mt="xl" label="Notes" {...form.getInputProps("notes")} />}
    </>
  );
}
